import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  CheckCircle2,
  XCircle,
  CreditCard,
  Loader2,
} from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import BookingHeader from '@features/booking/components/BookingHeader';
import { SummaryRow } from '@features/booking/components/SummaryRow';
import { BookingChrome } from '@features/booking/layouts';
import BookingFooter from '@features/booking/components/BookingFooter';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useStripeV2, getStripeErrorMessage } from '@features/booking/hooks/useStripeV2';
import type { StripeTokenError } from '@features/booking/hooks/useStripeV2';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElements, StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { forceLoadStripeV3, getStripePromise } from '@features/booking/hooks/useStripeV3';
import { handle3dsChallenge, ThreeDsError } from '@features/booking/hooks/handle3dsChallenge';
import { getCardElementOptions } from '@features/booking/constants/stripe';
import { useMercadoPagoSDK } from '@features/booking/hooks/useMercadoPagoSDK';
import { encryptCardData } from '@features/booking/utils/encryptCardData';
import {
  computeAmexChange,
  getStripePublishableKeyForTokenAccount,
  getStripeTokenAccount,
} from '@features/booking/utils/amexChange';
import { env } from '@core/config/env';
import { generateRandomDocumentId } from '@shared/utils/randomDocument';
import { capturePaymentError } from '@core/sentry';
import {
  getReservationData,
  saveGuarantee,
} from '../api/reservations';
import type { ReservationDataResponse } from '../api/reservations';
import type { StripeTokenAccount } from '../state/types';

const GUARANTEE_TYPE_DEPOSIT = 'deposit';
const GUARANTEE_TYPE_NO_SHOW = 'no_show';

function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length >= 3) {
    return digits.slice(0, 2) + '/' + digits.slice(2, 4);
  }
  return digits;
}

function formatReservationDate(resDate: string): string {
  try {
    const datePart = resDate.split(' ')[0];
    const date = new Date(datePart + 'T00:00:00');
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return resDate;
  }
}

function formatReservationTime(resTime: string): string {
  try {
    const parts = resTime.split(' ');
    const timePart = parts.length > 1 ? parts[1] : parts[0];
    const [h, m] = timePart.split(':');
    return `${h}:${m}`;
  } catch {
    return resTime;
  }
}

interface AcceptanceState {
  depositRequired: boolean;
  willBeCredited: boolean;
  cancelReservation: boolean;
  honorReservation: boolean;
  wontBeCharged: boolean;
  termsAndConditions: boolean;
}

interface ConfirmGuaranteePageProps {
  stripeTokenAccount?: StripeTokenAccount;
  stripePublishableKey: string;
  onStripeCardBrandChange?: (cardBrand: string | null) => void;
  onGuaranteeTypeChange?: (guaranteeType: string | undefined) => void;
}

function getRequiredAcceptances(guaranteeType: string | undefined, isWaitingList: boolean): (keyof AcceptanceState)[] {
  if (isWaitingList) {
    const wlKeys: (keyof AcceptanceState)[] = ['termsAndConditions'];
    if (guaranteeType === GUARANTEE_TYPE_DEPOSIT) wlKeys.unshift('depositRequired');
    if (guaranteeType === GUARANTEE_TYPE_NO_SHOW) wlKeys.unshift('honorReservation');
    return wlKeys;
  }
  const common: (keyof AcceptanceState)[] = ['cancelReservation', 'termsAndConditions'];
  if (guaranteeType === GUARANTEE_TYPE_DEPOSIT) {
    return ['depositRequired', 'willBeCredited', ...common];
  }
  if (guaranteeType === GUARANTEE_TYPE_NO_SHOW) {
    return ['honorReservation', 'wontBeCharged', ...common];
  }
  return common;
}

const ConfirmGuaranteePage = ({
  stripeTokenAccount = 'restaurant',
  stripePublishableKey,
  onStripeCardBrandChange,
  onGuaranteeTypeChange,
}: ConfirmGuaranteePageProps) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { t } = useTranslation('booking');
  const { t: tErrors } = useTranslation('errors');
  const restaurant = useRestaurant();

  // Payment SDKs
  const isMP = restaurant.paymentProcessor === 'mercadopago';
  // Stripe v2 only needed for MP's fallback token, not for Stripe restaurants
  const stripeV2Key = isMP ? env.stripeFallbackKey : '';
  const stripeV2 = useStripeV2(stripeV2Key);
  // Stripe v3 refs are populated by <StripeHooksBridge> below, which is only
  // rendered when the wrapper has mounted <Elements>. Calling useStripe() /
  // useElements() directly here would crash on MercadoPago restaurants because
  // their branch of the wrapper renders without an Elements provider.
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const [stripeReady, setStripeReady] = useState(false);
  const mpSDK = useMercadoPagoSDK();

  // Form refs (only for MP flow + MP hidden form)
  const stripeFormRef = useRef<HTMLFormElement>(null);
  const mpFormRef = useRef<HTMLFormElement>(null);

  // Card input state (MP uses custom inputs, Stripe uses CardElement)
  const [holderName, setHolderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState('');

  // Acceptance state
  const [acceptances, setAcceptances] = useState<AcceptanceState>({
    depositRequired: false,
    willBeCredited: false,
    cancelReservation: false,
    honorReservation: false,
    wontBeCharged: false,
    termsAndConditions: false,
  });
  const [showAcceptanceError, setShowAcceptanceError] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // MP-only: set after a ThreeDsError on the MP path. Stripe.js v3 loads
  // lazily inside handle3dsChallenge and overwrites window.Stripe, killing
  // the v2 instance used to tokenize the MP fallback card. Retry needs a
  // fresh page load. Stripe-v3 path does NOT set this — v3 was loaded from
  // the start, no v2 to break.
  const [requireReload, setRequireReload] = useState(false);

  // Random doc number for MP (matches legacy)
  const docNumberRef = useRef(
    generateRandomDocumentId().toString()
  );

  const cardOptions = useMemo(() => getCardElementOptions(), []);

  // Fetch reservation data
  const { data: reservation, isLoading, error: fetchError } = useQuery({
    queryKey: ['guaranteeReservation', token],
    queryFn: () => getReservationData(token!),
    enabled: !!token,
    retry: false,
    meta: { silentOn: [400, 401, 403, 404, 410, 422], source: 'confirm-guarantee-link' },
  });

  // Detect MP payment method on card number change
  useEffect(() => {
    if (!isMP || !mpSDK.isReady) return;
    const digits = cardNumber.replace(/\D/g, '');
    const bin = digits.slice(0, 6);
    if (bin.length >= 6) {
      mpSDK.getPaymentMethod(bin)
        .then(setPaymentMethodId)
        .catch(() => { /* non-critical */ });
    }
  }, [cardNumber, isMP, mpSDK.isReady, mpSDK.getPaymentMethod]);

  // Derived state
  const guaranteeType = reservation?.type;
  const tokenCreatedWithPlatformKey = stripeTokenAccount === 'platform';
  const isAlreadyPaid = reservation?.guaranteeStatus === 'paid';
  const isInvalid = fetchError || (reservation && !reservation.id);
  const sdksReady = isMP ? mpSDK.isReady : stripeReady;

  useEffect(() => {
    onGuaranteeTypeChange?.(guaranteeType);
  }, [guaranteeType, onGuaranteeTypeChange]);

  const isWaitingList = !!reservation?.inWaitingList;
  const feeAmount = reservation?.feeDeposit ?? 0;
  const feeAmountUSD = reservation?.feeDepositUSD ?? 0;
  const mustShowUSD =
    restaurant.currency &&
    restaurant.currency.toLowerCase() !== 'usd' &&
    feeAmountUSD > 0;

  // Cancellation policy — time-aware: check if cancellation window is still open
  const cancellationHours = reservation?.cancellationPolicy
    ? parseInt(reservation.cancellationPolicy, 10)
    : 0;
  const canCancelOrChange = (() => {
    if (cancellationHours <= 0) return false;
    const resDateStr = reservation?.resDate?.date;
    if (!resDateStr) return cancellationHours > 0;
    const reservationTime = new Date(resDateStr.replace(' ', 'T'));
    const deadline = new Date(Date.now() + cancellationHours * 60 * 60 * 1000);
    return deadline < reservationTime;
  })();

  const requiredAcceptances = getRequiredAcceptances(guaranteeType, isWaitingList);
  const allAccepted = requiredAcceptances.every((key) => acceptances[key]);

  // Parsed dates from reservation
  const displayDate = reservation?.resDate?.date
    ? formatReservationDate(reservation.resDate.date)
    : reservation?.date ?? '';
  const displayTime = reservation?.resTime?.date
    ? formatReservationTime(reservation.resTime.date)
    : reservation?.time ?? '';

  const toggleAcceptance = (key: keyof AcceptanceState) => {
    setAcceptances((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const syncHiddenForms = useCallback(() => {
    const digits = cardNumber.replace(/\D/g, '');
    const parts = expiry.split('/');
    const month = parts[0] || '';
    const year = parts[1] || '';

    // Sync Stripe v2 hidden form
    if (stripeFormRef.current) {
      const setVal = (id: string, val: string) => {
        const el = stripeFormRef.current!.querySelector(`#sv2-${id}`) as HTMLInputElement;
        if (el) el.value = val;
      };
      setVal('name', holderName);
      setVal('number', digits);
      setVal('exp-month', month);
      setVal('exp-year', year);
      setVal('cvc', cvc);
    }

    // Sync MercadoPago hidden form
    if (isMP && mpFormRef.current) {
      const setVal = (id: string, val: string) => {
        const el = mpFormRef.current!.querySelector(`#mp-${id}`) as HTMLInputElement;
        if (el) el.value = val;
      };
      setVal('cardholderName', holderName);
      setVal('cardNumber', digits);
      setVal('securityCode', cvc);
      setVal('cardExpirationMonth', month);
      setVal('cardExpirationYear', year);
      setVal('docType', 'Otro');
      setVal('docNumber', docNumberRef.current);
    }
  }, [holderName, cardNumber, expiry, cvc, isMP]);

  const validateForm = (): boolean => {
    if (!holderName.trim()) {
      setError(t('FORM_ERROR_CREDIT_CARD_NAME'));
      return false;
    }

    if (isMP) {
      // MP uses custom inputs — validate manually
      const digits = cardNumber.replace(/\D/g, '');
      if (digits.length < 13 || digits.length > 19) {
        setError(t('FORM_ERROR_CREDIT_CARD'));
        return false;
      }
      const expiryRegex = /^(0[1-9]|1[0-2])\/(\d{2})$/;
      if (!expiryRegex.test(expiry)) {
        setError(t('FORM_ERROR_CREDIT_CARD_EXPIRES'));
        return false;
      }
      if (cvc.length < 3 || cvc.length > 4) {
        setError(t('FORM_ERROR_SECURITY_CODE'));
        return false;
      }
      const firstTwo = digits.slice(0, 2);
      const isAmex = firstTwo === '34' || firstTwo === '37';
      // Mirror legacy deposit.js: error only when guarantee is deposit (the
      // restaurant's Stripe BR account cannot capture AMEX). For non-deposit
      // the AMEXChange path below re-routes the token to the USA platform.
      if (isAmex && !restaurant.allowAMEXBR && guaranteeType === GUARANTEE_TYPE_DEPOSIT) {
        setError(t('STRIPE_BRAZIL_AMEX_ERROR'));
        return false;
      }
    } else {
      // Stripe uses CardElement — validation is handled by the element
      if (!cardComplete) {
        setError(t('FORM_ERROR_CREDIT_CARD'));
        return false;
      }
    }

    if (!allAccepted) {
      setShowAcceptanceError(true);
      setError(t('CHECK_TERMS_CONDITIONS'));
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (isSubmitting || !reservation) return;

    setIsSubmitting(true);
    setError(null);
    setShowAcceptanceError(false);

    // Tracks whether this submit attempt loaded Stripe.js v3 during the
    // MP flow (via getStripePromise inside handle3dsChallenge). Used by
    // the outer catch to gate `setRequireReload` — protects against the
    // edge case where 3DS succeeded but a subsequent step threw. Once v3
    // loads, v2 is dead globally; retry without reload is broken.
    let mpFlow3dsLoaded = false;

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    syncHiddenForms();

    try {
      if (isMP) {
        // MercadoPago flow: Stripe best-effort + MP primary + DES encryption
        let cardTokenStripe = '';
        const digits = cardNumber.replace(/\D/g, '');
        const firstTwo = digits.slice(0, 2);
        const isAmex = firstTwo === '34' || firstTwo === '37';
        const changeStripeAmexBR = isAmex && !restaurant.allowAMEXBR && guaranteeType !== GUARANTEE_TYPE_DEPOSIT;

        // Step 1: Stripe v2 best-effort
        try {
          const stripeResponse = await stripeV2.createToken(stripeFormRef.current!);
          cardTokenStripe = stripeResponse.id || '';

          // Debit card check (Brazil only)
          if (stripeResponse.card?.funding === 'debit' && restaurant.defaultLanguage === 'pt') {
            setError(t('STRIPE_BRAZIL_DEBIT_CARD_ERROR'));
            setIsSubmitting(false);
            return;
          }
        } catch {
          // Stripe v2 token is best-effort for MercadoPago flow
        }

        // Step 2: MP token
        const mpToken = await mpSDK.createToken(mpFormRef.current!);

        // Step 3: Encrypt card data
        const parts = expiry.split('/');
        const encrypted = encryptCardData({
          number: digits,
          expiryMonth: parts[0],
          expiryYear: parts[1],
          cvc,
          holderName,
        });

        // Step 4: POST /save-guarantee
        const response = await saveGuarantee({
          reservation_id: reservation.id,
          token: token ?? undefined,
          cardToken: mpToken,
          AMEXChange: changeStripeAmexBR,
          paymentMethod: paymentMethodId || '',
          cardTokenStripe,
          temp1: encrypted.temp1,
          temp3: encrypted.temp3,
          temp4: encrypted.temp4,
          temp6: encrypted.temp6,
        });

        // Step 5: handle 3DS if BE returned requires_action.
        //
        // forceLoadStripeV3 unloads the v2 SDK (set by useStripeV2 above for
        // legacy tokenization) and loads v3 fresh — required because plain
        // loadStripe() would detect the v2 global and return v2, breaking
        // handleNextAction. See forceLoadStripeV3 docstring for full rationale.
        //
        // Pubkey rationale: BE creates the MP-fallback Stripe intent on the
        // platform (new-account) credentials. env.stripeFallbackKey matches
        // — same key the useStripeV2 hook above already uses. Do NOT pass
        // the stripePublishableKey prop here: for MP restaurants the wrapper
        // resolves that prop to restaurant.publicKeyPayment, which is a
        // MercadoPago key — not a Stripe key — and would crash handleNextAction.
        if (response.requires_action) {
          mpFlow3dsLoaded = true;
          await handle3dsChallenge(
            response,
            forceLoadStripeV3(env.stripeFallbackKey),
          );
        }
      } else {
        // Stripe v3 flow with CardElement
        const stripe = stripeRef.current;
        const elements = elementsRef.current;
        if (!stripe || !elements) throw new Error('Stripe not loaded');

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('CardElement not found');

        const { error: tokenError, token: stripeToken } = await stripe.createToken(cardElement, {
          name: holderName,
        });

        if (tokenError || !stripeToken) {
          setError(getStripeErrorMessage(
            { code: tokenError?.code, message: tokenError?.message || t('PAYMENT_CARD_ERROR') },
            t,
          ));
          setIsSubmitting(false);
          return;
        }

        const expectedStripeTokenAccount = getStripeTokenAccount({
          cardBrand: stripeToken.card?.brand,
          allowAMEXBR: restaurant.allowAMEXBR,
          guaranteeType,
        });
        if (expectedStripeTokenAccount !== stripeTokenAccount) {
          onStripeCardBrandChange?.(stripeToken.card?.brand ?? null);
          setError(t('PAYMENT_CARD_ERROR'));
          setIsSubmitting(false);
          return;
        }

        if (stripeToken.card?.brand === 'American Express' && !restaurant.allowAMEXBR && guaranteeType === GUARANTEE_TYPE_DEPOSIT) {
          setError(t('STRIPE_BRAZIL_AMEX_ERROR'));
          setIsSubmitting(false);
          return;
        }

        // Debit card check (Brazil only)
        if (stripeToken.card?.funding === 'debit' && restaurant.defaultLanguage === 'pt') {
          setError(t('STRIPE_BRAZIL_DEBIT_CARD_ERROR'));
          setIsSubmitting(false);
          return;
        }

        const response = await saveGuarantee({
          reservation_id: reservation.id,
          token: token ?? undefined,
          cardToken: stripeToken.id,
          AMEXChange: computeAmexChange({
            cardBrand: stripeToken.card?.brand,
            allowAMEXBR: restaurant.allowAMEXBR,
            guaranteeType,
            tokenCreatedWithPlatformKey,
          }),
          paymentMethod: stripeToken.card?.brand || '',
        });

        // 3DS handling — throws on failure, falls through on success
        await handle3dsChallenge(
          response,
          getStripePromise(stripePublishableKey),
        );
      }

      setSuccess(true);
    } catch (err) {
      // MP-only reload guard: if this submit loaded Stripe.js v3 (via
      // handle3dsChallenge with requires_action=true), v2 is dead globally
      // and any retry without page reload would silently produce a bad
      // token. Set the reload UX regardless of error type. Stripe-v3
      // path doesn't trigger this — v3 was loaded from the start by the
      // CardElement, no v2 to break.
      if (mpFlow3dsLoaded) {
        setRequireReload(true);
      }
      if (err instanceof ThreeDsError) {
        setError(tErrors(err.code));
      } else {
        const stripeErr = err as StripeTokenError;
        setError(getStripeErrorMessage({ code: stripeErr.code, message: stripeErr.message }, t));
      }
      capturePaymentError(err, isMP ? 'mercadopago' : 'stripe', {
        source: 'confirm-guarantee-submit',
        reservationId: reservation?.id,
        guaranteeType,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // -- Render helpers --

  const renderLoading = () => (
    <div className="text-center text-foreground/40 text-sm py-12">
      <Loader2 size={24} className="animate-spin mx-auto mb-2" />
      {t('CONFIRM_GUARANTEE_LOADING')}
    </div>
  );

  const renderNoToken = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <XCircle className="text-red-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">
          {t('INVALID_TOKEN_TITLE')}
        </h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">
          {t('INVALID_TOKEN_MESSAGE')}
        </p>
      </div>
      <div className="w-full max-w-sm px-4">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('CONFIRM_GUARANTEE_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderAlreadyPaid = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
          <CheckCircle2 className="text-green-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">
          {t('CONFIRM_GUARANTEE_ALREADY_PAID_TITLE')}
        </h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">
          {t('CONFIRM_GUARANTEE_ALREADY_PAID_SUBTITLE')}
        </p>
      </div>
      {renderReservationSummary()}
      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('CONFIRM_GUARANTEE_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderSuccess = () => (
    <>
      <div className="flex flex-col items-center pt-4">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
          <CheckCircle2 className="text-green-600" size={32} />
        </div>
        <h1 className="text-3xl font-display mb-2 text-center">
          {t('THANK_YOU')}
        </h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">
          {t('CONFIRM_GUARANTEE_SUCCESS_SUBTITLE')}
        </p>
      </div>
      {renderReservationSummary()}
      <div className="w-full max-w-sm px-4 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
          onClick={() => navigate('/')}
        >
          {t('CONFIRM_GUARANTEE_BACK_TO_HOME')}
        </Button>
      </div>
    </>
  );

  const renderReservationSummary = () => {
    if (!reservation) return null;
    return (
      <div className="w-full max-w-sm px-4 space-y-4">
        <div className="bg-foreground/5 rounded-xl p-4 space-y-3">
          {reservation.guestName && (
            <SummaryRow label={t('CONFIRM_GUARANTEE_GUEST')} value={reservation.guestName} />
          )}
          <SummaryRow label={t('CONFIRM_GUARANTEE_DATE')} value={displayDate} />
          <SummaryRow label={t('CONFIRM_GUARANTEE_TIME')} value={displayTime} />
          <SummaryRow label={t('CONFIRM_GUARANTEE_GUESTS')} value={reservation.partySize} />
        </div>
      </div>
    );
  };

  const renderAcceptanceButton = (
    key: keyof AcceptanceState,
    label: string,
    description?: string,
  ) => (
    <div
      key={key}
      className={`flex items-start justify-between gap-3 p-3 rounded-xl border transition-colors ${
        acceptances[key]
          ? 'border-brand/30 bg-brand/5'
          : showAcceptanceError && !acceptances[key]
            ? 'border-red-300 bg-red-50'
            : 'border-border'
      }`}
    >
      <div className="flex-1 text-sm">
        <p className="text-foreground/80">{label}</p>
        {description && (
          <p className="text-foreground/50 text-xs mt-1">{description}</p>
        )}
      </div>
      <button
        type="button"
        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          acceptances[key]
            ? 'bg-brand text-brand-foreground'
            : 'bg-foreground/10 text-foreground/60 hover:bg-foreground/20'
        }`}
        onClick={() => toggleAcceptance(key)}
      >
        {acceptances[key]
          ? t('STRING_ACCEPTED')
          : t('STRING_ACCEPT')}
      </button>
    </div>
  );

  const renderForm = () => {
    if (!reservation) return null;

    return (
      <>
        <div className="flex flex-col items-center pt-4">
          <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mb-6">
            <CreditCard className="text-brand" size={32} />
          </div>
          <h1 className="text-3xl font-display mb-2 text-center">
            {t('COMPLETE_YOUR_RESERVATION')}
          </h1>
          <p className="text-foreground/50 text-sm mb-4 text-center">
            {t('CONFIRM_GUARANTEE_SUBTITLE')}
          </p>
        </div>

        {/* WL-specific payment instructions */}
        {isWaitingList && (
          <div className="w-full max-w-sm px-4 space-y-2 text-sm text-foreground/80">
            <p>{t('WAITING_LIST_PAYMENT_PROVIDE_CC')}</p>
            <p>{t('WAITING_LIST_PAYMENT_GUARANTEE_NOT_APPLY')}</p>
            <p>{t('WAITING_LIST_PAYMENT_NO_SHOW_APPLIES')}</p>
            <p>{t('WAITING_LIST_PAYMENT_NOT_CHARGED')}</p>
          </div>
        )}

        {/* Reservation summary */}
        {renderReservationSummary()}

        <div className="w-full max-w-sm px-4 mt-6 space-y-5">
          {/* Fee display */}
          <div className="bg-foreground/5 rounded-xl p-4">
            <div className="flex justify-between text-sm">
              <span className="text-foreground/70">
                {guaranteeType === GUARANTEE_TYPE_DEPOSIT
                  ? t('CONFIRM_GUARANTEE_DEPOSIT_AMOUNT')
                  : t('CONFIRM_GUARANTEE_AMOUNT')}
              </span>
              <div className="text-right">
                <span className="font-semibold text-foreground">
                  {restaurant.currency} {feeAmount.toLocaleString()}
                </span>
                {mustShowUSD && (
                  <span className="block text-xs text-foreground/50">
                    USD {feeAmountUSD.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Acceptance conditions */}
          <div className="space-y-2">
            {isWaitingList ? (
              <>
                {/* WL uses a single condition per guarantee type instead of the normal pair */}
                {guaranteeType === GUARANTEE_TYPE_DEPOSIT && renderAcceptanceButton(
                  'depositRequired',
                  t('WAITING_LIST_PAYMENT_CONDITION_DEPOSIT', {
                    currency: restaurant.currency,
                    amount: feeAmount.toLocaleString() +
                      (mustShowUSD ? ` (USD ${feeAmountUSD.toLocaleString()})` : ''),
                  }),
                )}
                {guaranteeType === GUARANTEE_TYPE_NO_SHOW && renderAcceptanceButton(
                  'honorReservation',
                  t('WAITING_LIST_PAYMENT_CONDITION_NO_SHOW', {
                    currency: restaurant.currency,
                    amount: feeAmount.toLocaleString() +
                      (mustShowUSD ? ` (USD ${feeAmountUSD.toLocaleString()})` : ''),
                  }),
                )}
              </>
            ) : (
              <>
                {guaranteeType === GUARANTEE_TYPE_DEPOSIT && (
                  <>
                    {renderAcceptanceButton(
                      'depositRequired',
                      t('CONFIRM_GUARANTEE_DEPOSIT_REQUIRED'),
                      `${restaurant.currency} ${feeAmount.toLocaleString()}` +
                        (mustShowUSD ? ` (USD ${feeAmountUSD.toLocaleString()})` : ''),
                    )}
                    {renderAcceptanceButton(
                      'willBeCredited',
                      t('CONFIRM_GUARANTEE_WILL_BE_CREDITED'),
                    )}
                  </>
                )}

                {guaranteeType === GUARANTEE_TYPE_NO_SHOW && (
                  <>
                    {renderAcceptanceButton(
                      'honorReservation',
                      t('CONFIRM_GUARANTEE_HONOR_RESERVATION'),
                      `${restaurant.currency} ${feeAmount.toLocaleString()}` +
                        (mustShowUSD ? ` (USD ${feeAmountUSD.toLocaleString()})` : ''),
                    )}
                    {renderAcceptanceButton(
                      'wontBeCharged',
                      t('CONFIRM_GUARANTEE_WONT_BE_CHARGED'),
                    )}
                  </>
                )}

                {renderAcceptanceButton(
                  'cancelReservation',
                  canCancelOrChange
                    ? t('CONFIRM_GUARANTEE_CAN_CANCEL', { hours: cancellationHours })
                    : t('CANT_CHANGE_RESERVATION'),
                )}
              </>
            )}

            {renderAcceptanceButton(
              'termsAndConditions',
              t('TERMS_ACCEPT_CHECKBOX'),
            )}
          </div>

          {/* Card inputs */}
          {/* Mount outside the sdksReady gate so it can flip stripeReady on.
              For MP restaurants the wrapper skips <Elements> entirely, so
              <StripeHooksBridge> is only safe to render when !isMP. */}
          {!isMP && (
            <StripeHooksBridge
              stripeRef={stripeRef}
              elementsRef={elementsRef}
              onReady={setStripeReady}
            />
          )}
          {!sdksReady ? (
            <div className="text-center text-foreground/50 text-sm py-4">
              {(isMP ? mpSDK.error : null)
                ? t('PAYMENT_SDK_LOAD_ERROR')
                : t('PAYMENT_LOADING')}
            </div>
          ) : (
            <div className="space-y-4">
              {!isWaitingList && (
                <p className="text-foreground/60 text-sm">
                  {t('NEW_MODAL_PROVIDE_CC_DETAILS')}
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground/70 mb-1">
                  {t('PAYMENT_LABEL_CC_2')}
                </label>
                <input
                  type="text"
                  className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                  value={holderName}
                  onChange={(e) => setHolderName(e.target.value)}
                  placeholder="John Doe"
                  disabled={isSubmitting}
                />
              </div>

              {isMP ? (
                <>
                  {/* MercadoPago: custom card inputs */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/70 mb-1">
                      {t('PAYMENT_LABEL_CC')}
                    </label>
                    <input
                      type="text"
                      className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                      placeholder="4242 4242 4242 4242"
                      maxLength={23}
                      inputMode="numeric"
                    />
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-foreground/70 mb-1">
                        {t('STRING_PAYMENT_EXPIRES')}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                        value={expiry}
                        onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                        placeholder="MM/YY"
                        maxLength={5}
                        inputMode="numeric"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-foreground/70 mb-1">
                        {t('STRING_SECURITY_CODE')}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                        value={cvc}
                        onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="123"
                        maxLength={4}
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Stripe: CardElement (hosted iframe) */}
                  <div>
                    <label className="block text-sm font-medium text-foreground/70 mb-1">
                      {t('PAYMENT_LABEL_CC')}
                    </label>
                    <div className="border border-border rounded-xl px-4 py-3 focus-within:ring-2 focus-within:ring-brand/20 focus-within:border-brand">
                      <CardElement
                        options={cardOptions}
                        onChange={(event: StripeCardElementChangeEvent) => {
                          setCardComplete(event.complete);
                          if (event.brand !== 'unknown') {
                            onStripeCardBrandChange?.(event.brand);
                          }
                          if (event.error) setError(event.error.message);
                          else setError(null);
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Hidden Stripe v2 form (only for MP Stripe fallback) */}
          {isMP && (
            <form ref={stripeFormRef} name="stripeForm" style={{ display: 'none' }}>
              <input id="sv2-name" data-stripe="name" type="hidden" />
              <input id="sv2-number" data-stripe="number" type="hidden" />
              <input id="sv2-exp-month" data-stripe="exp-month" type="hidden" />
              <input id="sv2-exp-year" data-stripe="exp-year" type="hidden" />
              <input id="sv2-cvc" data-stripe="cvc" type="hidden" />
            </form>
          )}

          {/* Hidden MercadoPago form (only for MP processor) */}
          {isMP && (
            <form ref={mpFormRef} name="mercadoPagoForm" style={{ display: 'none' }}>
              <input id="mp-cardholderName" data-checkout="cardholderName" type="hidden" />
              <input id="mp-cardNumber" data-checkout="cardNumber" type="hidden" />
              <input id="mp-securityCode" data-checkout="securityCode" type="hidden" />
              <input id="mp-cardExpirationMonth" data-checkout="cardExpirationMonth" type="hidden" />
              <input id="mp-cardExpirationYear" data-checkout="cardExpirationYear" type="hidden" />
              <input id="mp-docType" data-checkout="docType" type="hidden" defaultValue="Otro" />
              <input id="mp-docNumber" data-checkout="docNumber" type="hidden" defaultValue={docNumberRef.current} />
            </form>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 text-red-700 rounded-xl p-4 text-sm">
              {error}
            </div>
          )}

          {/* Submit button */}
          <Button
            className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
            onClick={handleSubmit}
            disabled={!sdksReady || isSubmitting || requireReload}
          >
            {requireReload ? (
              tErrors('PAYMENT_3DS_RELOAD_TO_RETRY')
            ) : isSubmitting ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                {t('PAYMENT_PROCESSING')}
              </>
            ) : (
              t('CONFIRM_GUARANTEE_SUBMIT')
            )}
          </Button>
        </div>
      </>
    );
  };

  return (
    <BookingChrome flexCol>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1">
          {!token && renderNoToken()}
          {token && isLoading && renderLoading()}
          {token && !isLoading && isInvalid && renderNoToken()}
          {token && !isLoading && !isInvalid && success && renderSuccess()}
          {token && !isLoading && !isInvalid && !success && isAlreadyPaid && renderAlreadyPaid()}
          {token && !isLoading && !isInvalid && !success && !isAlreadyPaid && reservation && renderForm()}
        </main>

        <footer className="border-t border-border">
          <div className="flex items-center px-6 py-4">
            <button
              aria-label="Go back"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={() => navigate('/')}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

/**
 * Reads useStripe() / useElements() and hands the instances up to the page
 * component through refs. Must only render under an <Elements> provider, which
 * is why it lives in the Stripe branch of the JSX — the MercadoPago branch
 * skips the wrapper entirely and the hooks would throw there.
 */
interface StripeHooksBridgeProps {
  stripeRef: React.MutableRefObject<Stripe | null>;
  elementsRef: React.MutableRefObject<StripeElements | null>;
  onReady: (ready: boolean) => void;
}

function StripeHooksBridge({ stripeRef, elementsRef, onReady }: StripeHooksBridgeProps) {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    stripeRef.current = stripe;
    elementsRef.current = elements;
    onReady(!!stripe && !!elements);
  }, [stripe, elements, stripeRef, elementsRef, onReady]);

  return null;
}

function ConfirmGuaranteePageWrapper() {
  const restaurant = useRestaurant();
  const isMP = restaurant.paymentProcessor === 'mercadopago';
  const [cardBrand, setCardBrand] = useState<string | null>(null);
  const [guaranteeType, setGuaranteeType] = useState<string | undefined>();

  const stripeTokenAccount = getStripeTokenAccount({
    cardBrand,
    allowAMEXBR: restaurant.allowAMEXBR,
    guaranteeType,
  });
  const stripePublishableKey = getStripePublishableKeyForTokenAccount({
    tokenAccount: stripeTokenAccount,
    restaurantPublishableKey: restaurant.publicKeyPayment,
    platformPublishableKey: env.stripeFallbackKey,
  });
  const stripePromise = useMemo(
    () => isMP ? null : getStripePromise(stripePublishableKey),
    [isMP, stripePublishableKey],
  );

  if (isMP || !stripePromise) {
    return (
      <ConfirmGuaranteePage
        stripeTokenAccount={stripeTokenAccount}
        stripePublishableKey={stripePublishableKey}
        onStripeCardBrandChange={setCardBrand}
        onGuaranteeTypeChange={setGuaranteeType}
      />
    );
  }

  return (
    <Elements key={stripePublishableKey} stripe={stripePromise}>
      <ConfirmGuaranteePage
        stripeTokenAccount={stripeTokenAccount}
        stripePublishableKey={stripePublishableKey}
        onStripeCardBrandChange={setCardBrand}
        onGuaranteeTypeChange={setGuaranteeType}
      />
    </Elements>
  );
}

export default ConfirmGuaranteePageWrapper;
